/**
 * 링크 예산·글 예산을 계정별로 접는다 — 강제추가 글의 작성 계정에 그 링크의 예산을, 글 예산은 그 글의 계정 줄에 더한다.
 *
 * - 등록 계정의 글이면 그 계정 예산에 더한다.
 * - 등록 계정이 아니면 계정 줄을 새로 붙인다(예산 = 그 계정 링크들의 합). 회수율 분모에도 들어간다.
 * - 아직 수집되지 않은 링크는 글쓴 계정을 몰라 어느 줄에도 붙지 않는다 — 총 예산에만 들어간다
 *   (`sumForcedAdditionBudgets`).
 * - 글 예산(`postBudgets`)은 계정 줄을 새로 붙이지 않는다 — 글이 붙는 줄(`groupCampaignPostsByAccountRows`)이 있으면
 *   그 첫 줄에 더하고, 없으면(캠페인 계정이 아닌 글쓴이 · 지금 안 보이는 글) 총 예산에만 들어간다.
 *
 * ⚠️ 등록 계정의 모양은 그대로 둔다(문자열 `platform*account` 도 받는다) — 조회 필터가 같은 배열을
 *    읽으므로, 이 결과는 예산·계정 수·계정별 표에만 쓴다. 필터에 넘기면 수집 범위가 넓어진다.
 */

import { matchCampaignLink } from './campaign-link-identity';
import { campaignPostBudgetIdentity } from './campaign-sources';
import { normalizeCampaignAccountName, groupCampaignPostsByAccountRows } from './campaign-account-posts';

/** 접은 뒤의 계정 줄. 문자열 계정은 원래 모양 그대로 남는다 */
export type FoldedCampaignAccount = { platform: string; account: string; budget: number } | string;

/**
 * @param accounts       캠페인 등록 계정(`buildCampaignCriteria().accounts` 모양)
 * @param forcedPosts    그 캠페인의 강제추가 글(제외 글을 뺀 것)
 * @param budgetsByLink  `readForcedAdditionBudgets` 결과
 * @param postBudgets    글 예산 — `posts` 는 세는 글(제외 글을 뺀 것), `budgetsByLink` 는 `readCampaignPostBudgets` 결과(«같은 글» 열쇠 → 예산)
 * @returns 등록 계정(예산 더함) + 등록되지 않은 강제추가 계정
 */
export function foldForcedLinkBudgetsIntoAccounts(
  accounts: readonly unknown[] | null | undefined,
  forcedPosts: readonly any[] | null | undefined,
  budgetsByLink: ReadonlyMap<string, number>,
  postBudgets?: { posts: readonly any[]; budgetsByLink: ReadonlyMap<string, number> },
): FoldedCampaignAccount[] {
  const out: FoldedCampaignAccount[] = [];
  const indexByKey = new Map<string, number>();

  (Array.isArray(accounts) ? accounts : []).forEach((acc: any) => {
    if (acc && typeof acc === 'object') {
      const platform = String(acc.platform || acc.platformId || '').trim();
      const account = String(acc.account || acc.username || '').trim();
      if (!account) return;
      indexByKey.set(accountKey(platform, account), out.length);
      out.push({ platform, account, budget: Number(acc.budget) || 0 });
      return;
    }
    const str = String(acc || '').trim();
    if (!str) return;
    const [platform, account] = str.includes('*') ? str.split('*') : ['', str];
    indexByKey.set(accountKey(platform, account), out.length);
    out.push(str);
  });

  const seenLinks = new Set<string>();
  (Array.isArray(forcedPosts) ? forcedPosts : []).forEach((post) => {
    const platform = String(post?.source || post?.platform || '').trim();
    const account = String(post?.account || post?.username || '').trim();
    if (!platform || !account) return;
    // 글 링크는 등록 링크와 모양이 다를 수 있다(리다이렉트 사본 · `/reel/`) — 같은 글로 맞춘다. 같은 링크의 예산은 처음 글 한 번만
    const link = matchCampaignLink(post, budgetsByLink);
    const budget = link && !seenLinks.has(link) ? budgetsByLink.get(link) ?? 0 : 0;
    if (link) seenLinks.add(link);

    const key = accountKey(platform, account);
    const at = indexByKey.get(key);
    if (at === undefined) {
      indexByKey.set(key, out.length);
      out.push({ platform, account, budget });
      return;
    }
    const row = out[at];
    if (typeof row === 'object') row.budget += budget;
  });

  if (postBudgets && postBudgets.budgetsByLink.size > 0) foldPostBudgets(out, postBudgets.posts, postBudgets.budgetsByLink);
  return out;
}

/** 글 예산을 그 글이 붙는 첫 객체 줄에 더한다 — 같은 글(«같은 글» 열쇠)은 처음 글 한 번만 */
function foldPostBudgets(out: FoldedCampaignAccount[], posts: readonly any[], budgetsByLink: ReadonlyMap<string, number>): void {
  const seen = new Set<string>();
  const budgeted = new Map<any, number>();
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const identity = campaignPostBudgetIdentity(post?.link || post?.url || '');
    if (!identity || seen.has(identity) || !budgetsByLink.has(identity)) return;
    seen.add(identity);
    budgeted.set(post, budgetsByLink.get(identity) ?? 0);
  });
  if (budgeted.size === 0) return;
  const groups = groupCampaignPostsByAccountRows(out, [...budgeted.keys()]);
  const placed = new Set<any>();
  out.forEach((row, i) => {
    if (typeof row !== 'object') return;
    groups[i].forEach((post) => {
      if (placed.has(post)) return;
      placed.add(post);
      row.budget += budgeted.get(post) ?? 0;
    });
  });
}

/** 소문자 `채널*계정` — 계정 이름은 앞 `@` 하나를 뗀다(`normalizeCampaignAccountName`) */
function accountKey(platform: string, account: string): string {
  return `${String(platform || '').trim().toLowerCase()}*${normalizeCampaignAccountName(account)}`;
}
